import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { useLang } from "@/lib/i18n-context";
import { getDepositFor } from "@/lib/deposits";
import { toast } from "sonner";
import { Loader2, ArrowRight, ShieldCheck, ClipboardList, CreditCard, CheckCircle2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FORM_CONFIGS } from "@/lib/form-configs";

const STATUS_COLOR = {
  submitted: "bg-blue-100 text-blue-800 border-blue-200",
  reviewed: "bg-amber-100 text-amber-800 border-amber-200",
  approved: "bg-green-100 text-green-800 border-green-200",
  rejected: "bg-red-100 text-red-800 border-red-200",
};

export default function DashboardPage() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const [items, setItems] = useState(null);
  const [payingId, setPayingId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/registrations/mine");
        setItems(data);
      } catch (e) {
        setItems([]);
      }
    })();
  }, []);

  const payDeposit = async (reg) => {
    const dep = getDepositFor(reg);
    if (!dep) return;
    setPayingId(reg.id);
    try {
      const { data } = await api.post("/payments/checkout", {
        registration_id: reg.id,
        lookup_key: dep.lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start checkout");
      setPayingId(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-16">
      <div className="flex items-center gap-4 mb-2">
        {user?.picture && (
          <img src={user.picture} alt="" className="w-14 h-14 rounded-full border-2 border-white shadow" />
        )}
        <div>
          <div className="text-xs uppercase tracking-widest text-brand-red font-semibold">Dashboard</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold" data-testid="dashboard-welcome">
            こんにちは, {user?.name?.split(" ")[0] || "friend"}
          </h1>
        </div>
      </div>
      <div className="text-sm text-muted-foreground mb-8 flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-brand-red" /> {t("privacy_note")}
      </div>

      {items === null ? (
        <div className="flex items-center gap-2 text-muted-foreground py-24 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading your registrations…
        </div>
      ) : items.length === 0 ? (
        <div className="bg-brand-canvas rounded-3xl p-10 md:p-16 text-center" data-testid="dashboard-empty">
          <div className="w-16 h-16 mx-auto rounded-full bg-white flex items-center justify-center mb-4">
            <ClipboardList className="w-7 h-7 text-brand-red" />
          </div>
          <h3 className="font-display text-2xl font-bold">No registrations yet</h3>
          <p className="text-muted-foreground mt-2 max-w-md mx-auto">
            Register as a performer, restaurant owner, trader, business matching participant or sponsor.
          </p>
          <Button asChild className="mt-6 rounded-full bg-brand-red hover:bg-brand-red-dark text-white" data-testid="dashboard-cta-register">
            <Link to="/register">Start a registration <ArrowRight className="w-4 h-4 ml-2" /></Link>
          </Button>
        </div>
      ) : (
        <div className="space-y-4" data-testid="dashboard-list">
          {items.map((r) => {
            const cfg = FORM_CONFIGS[r.category];
            const title = cfg ? cfg.title[lang] || cfg.title.en : r.category;
            const deposit = getDepositFor(r);
            const canPay = deposit && r.payment_status === "pending" && r.status !== "rejected";
            return (
              <div
                key={r.id}
                className="bg-white border border-border rounded-2xl p-6 flex flex-col md:flex-row md:items-center gap-4"
                data-testid={`reg-item-${r.id}`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Badge className={`${STATUS_COLOR[r.status]} border font-medium`}>{r.status}</Badge>
                    <Badge variant="outline" className="text-xs">
                      Payment: {r.payment_status}
                    </Badge>
                    {r.payment_status === "paid" && (
                      <span className="text-xs text-green-700 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Deposit received</span>
                    )}
                  </div>
                  <div className="font-display font-bold text-lg">{title}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    ID: {r.id} · Submitted {new Date(r.created_at).toLocaleString()}
                  </div>
                  {r.admin_note && (
                    <div className="mt-2 text-xs bg-brand-canvas rounded-lg px-3 py-2">
                      <span className="font-semibold">Organizer note:</span> {r.admin_note}
                    </div>
                  )}
                </div>

                <div className="text-xs text-muted-foreground max-w-sm">
                  {r.category === "performer" && r.data?.group_name && (
                    <div><strong>Group:</strong> {r.data.group_name}</div>
                  )}
                  {(r.category === "restaurant" || r.category === "trader") && r.data?.business_name && (
                    <div><strong>Business:</strong> {r.data.business_name}</div>
                  )}
                  {r.category === "sponsor" && r.data?.company_name && (
                    <div><strong>Company:</strong> {r.data.company_name} · {r.data.tier}</div>
                  )}
                  {r.category === "business_matching" && r.data?.org_name && (
                    <div><strong>Org:</strong> {r.data.org_name} · {r.data.role}</div>
                  )}
                </div>

                {canPay ? (
                  <Button
                    onClick={() => payDeposit(r)}
                    disabled={payingId === r.id}
                    className="rounded-full bg-brand-red hover:bg-brand-red-dark text-white gap-2 whitespace-nowrap"
                    data-testid={`pay-btn-${r.id}`}
                  >
                    {payingId === r.id ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4" />
                        Pay deposit ¥{deposit.amount_jpy.toLocaleString()}
                      </>
                    )}
                  </Button>
                ) : r.payment_status === "paid" ? (
                  <div className="text-xs px-4 py-2 rounded-full bg-green-100 text-green-800 border border-green-200 font-semibold whitespace-nowrap">
                    ¥ Deposit paid
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-10">
        <Button asChild variant="outline" className="rounded-full" data-testid="dashboard-add-more">
          <Link to="/register">+ Add another registration</Link>
        </Button>
      </div>
    </div>
  );
}
